import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import {
  ConfigService,
  FileEntity,
  Image,
  PrintBlockStockNameTypeRegex,
} from 'src/app/core';
import { OrderService } from 'src/app/feature/services';
import {
  getMainFileOrError,
  Order,
  OrderStatuses,
  PrintBlockItem,
} from 'src/app/shared';

import { SubSink } from 'subsink';
import { AdminOrderErrorReportComponent } from '../admin-order-error-report/admin-order-error-report.component';
import { AnnouncePiceThesisEditComponent } from './announce-pice-thesis-edit/announce-pice-thesis-edit.component';

const _ = require('lodash');

@Component({
  selector: 'app-admin-order-preview',
  templateUrl: './admin-order-preview.component.html',
  styleUrls: ['./admin-order-preview.component.sass'],
})
export class AdminOrderPreviewComponent implements OnInit {
  // ------------------------------------------- Public Properties|

  public images: Image[] = [];
  public type!: Order['type'];
  public getBaseUrl = () => this.configService.getBaseUrl();
  public statusFormController: FormControl = new FormControl(this.order.status);
  public mainFile: FileEntity = getMainFileOrError(this.order.files);

  // --------------------------------------------Private properties|

  private subsink = new SubSink();

  // --------------------------------------------Getters & Setters|

  public get isTwoSided(): boolean {
    return this.orderService.isOrderTwoSided(this.order);
  }

  public colorfulBlocks(): PrintBlockItem[] {
    return _.filter(
      getMainFileOrError(this.order?.files).fileBlocks,
      (item: any) =>
        PrintBlockStockNameTypeRegex.colorful.test(item?.stock?.type!)
    );
  }

  public get blackWhiteBlocks(): PrintBlockItem[] {
    return _.filter(
      getMainFileOrError(this.order?.files).fileBlocks,
      (item: any) =>
        PrintBlockStockNameTypeRegex.blackAndWhite.test(item?.stock?.type!)
    )!;
  }

  public get halfColorfulBlocks(): PrintBlockItem[] {
    return _.filter(
      getMainFileOrError(this.order?.files).fileBlocks,
      (item: any) =>
        PrintBlockStockNameTypeRegex.halfColorful.test(item?.stock?.type!)
    )!;
  }

  public get status(): string {
    switch (this.order.status) {
      case 'done':
        return 'انجام شده';

      case 'progress':
        return 'درحال انجام';

      case 'payment':
        return 'در انتظار پرداخت';

      case 'calculation':
        return 'در انتظار تخمین قیمت';

      default:
        return 'مشکل در تشخیص وضعیت';
    }
  }

  // ------------------------------------------------------Constructors|

  constructor(
    @Inject(MAT_DIALOG_DATA) public order: Order,
    private configService: ConfigService,
    private orderService: OrderService,
    private snackbar: MatSnackBar,
    private dialogBox: MatDialog
  ) {
    this.type = order.type;
  }

  ngOnInit(): void {}

  // ------------------------------------------------------Public Methods|

  public selectFile(name: string, file: any) {}

  public reportError() {
    this.subsink.sink = this.dialogBox
      .open(AdminOrderErrorReportComponent, {
        width: '100%',
        maxWidth: '400px',
        data: this.order,
      })
      .afterClosed()
      .subscribe(() => {});
  }

  public getOrderSize() {
    return this.orderService.getOrderPageSizes(this.order).join(' - ');
  }

  public setStatus(status: OrderStatuses) {
    this.subsink.sink = this.orderService
      .updateStatus(this.order.id, status)
      .pipe(
        catchError((e) => {
          this.snackbar.open('مشکل در آپدیت وضعیت سفارش');
          return throwError(() => new Error(e?.toString()));
        })
      )
      .subscribe((data: Order) => {
        this.snackbar.open('تغییر با موفقیت انجام شد', '', { duration: 1500 });
        this.statusFormController.setValue(data.status);
      });
  }

  /**
   * Open announce price dialog
   * to update price of order.
   */
  async openAnnouncePriceDialog() {
    if (this.order.type === 'thesis-edit') {
      const result: number = await firstValueFrom(
        this.dialogBox
          .open(AnnouncePiceThesisEditComponent, {
            data: { order: this.order },
            width: '400px',
            height: '400px',
          })
          .afterClosed()
      );

      if (result) {
        const priceUpdateResult: Order = await this.updatePrice(result);

        this.order.price = priceUpdateResult.price;
        this.order.status = priceUpdateResult.status;
        this.statusFormController.setValue(priceUpdateResult.status);

        this.snackbar.open('عملیات با موفقیت انجام شد.', '', {
          duration: 1000,
        });
      }
    }
  }

  /**
   * Update price of order.
   * @param newPrice New price you want to update
   */
  public async updatePrice(newPrice: number): Promise<Order> {
    try {
      const order = await firstValueFrom(
        this.orderService.announcePrice(this.order.id, newPrice)
      );

      this.order.price = (order as any).price;

      return order as any;
    } catch (e) {
      throw e;
    }
  }

  getDate(): any {}
  ngOnDestroy(): any {}
}
